import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { TypographyStyledBlack } from '../../../shared/components/tileLinkStyled/style';
import { Box, Grid2, Typography } from '@mui/material';
import { BlueHeaderWithText } from '../../estimatingRequirements/peng2018/blueHeaderWithText';
import { MixedModal } from '../../../shared/components/modal/MixedModal';
import { metaDataSelector } from '../../../store/globalSelectors';
import { useAppSelector } from '../../../store/hooks';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { ArcContainer } from '../../../shared/components/arcContainer';
import { SingleNumberCard } from '../../../shared/components/numberInput/numberCards/singleNumberCard';
import { DoubleTextBlueCard } from '../../../shared/components/doubleTextBlueCard';
import { setProteinCalc } from '../../../store/slices/metaDataSlice';

import { TextWithInfoIcon } from '../../../shared/components/textWithInfoIcon';
import InfoIcon from '../../../assets/icons/infoIcon.svg';
import ProteinRequirementIcon from '../../../assets/icons/proteinRequirementIcon.svg';
import ImproveTreatment from '../../../assets/icons/improveTreatment.svg';
import ProteinRequirementImage from '../../../assets/images/ProteinRequirementImage.png';
import InfoIconTwo from '../../../assets/icons/infoIconTwo.svg';
import { calculateMultiplication } from '../../../shared/utils/utilFunctions';

export const Protein = () => {
  const dispatch = useDispatch();
  const [openImageInfoModal, setOpenImageInfoModal] = useState(false);
  const { patientInformation, estimatingRequirements } =
    useAppSelector(metaDataSelector);
  const { metric } = patientInformation;
  const { weight, estimatedDryWeight } = metric;
  const { protein } = estimatingRequirements;
  const { min, max, calculatedMin, calculatedMax } = protein;

  const consideredWeight = estimatedDryWeight > 0 ? estimatedDryWeight : weight;

  useEffect(() => {
    dispatch(
      setProteinCalc({
        ...protein,
        calculatedMin: calculateMultiplication(min, consideredWeight),
        calculatedMax: calculateMultiplication(max, consideredWeight)
      })
    );
  }, [min, max]);

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          backgroundColor: '#F2F2F2',
          p: 0,
          top: 0,
          left: 0,
          height: '100%'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            marginTop: '1.875rem'
          }}
        >
          <TypographyStyledBlack sx={{ margin: '3.125rem 0' }}>
            PROTEIN
          </TypographyStyledBlack>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <BlueHeaderWithText
              text1={'Weight (kg):'}
              value1={String(consideredWeight)}
              single={true}
              customWidth="75%"
              customMarginBottom="1rem"
              backgroundColor="#5BC2E780"
            />
          </Box>
          <ArcContainer icon={ProteinRequirementIcon} customizedHeight="auto">
            <TextWithInfoIcon
              title={'Protein Requirement'}
              icon={InfoIcon}
              fontSize={'1.125rem'}
              lineHeight={'1.375rem'}
              variant={'h4'}
              handleIconClick={() => setOpenImageInfoModal(true)}
              testId="ProteinInfoPopup"
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  fontFamily: 'Calibri',
                  fontSize: '0.75rem'
                }}
              >
                (g/kg bodyweight/day)
              </Typography>
            </Box>
            <Grid2 display={'flex'} flexDirection="row">
              <SingleNumberCard
                width={'50%'}
                title={''}
                marginVal={'0.625rem'}
                range={{ min: 0, max: 2.5 }}
                incrementDecrementVal={0.1}
                subHead={'(min)'}
                value={min}
                setValue={(val) => {
                  dispatch(setProteinCalc({ ...protein, min: val }));
                }}
              />
              <SingleNumberCard
                width={'50%'}
                title={''}
                subHead={'(max)'}
                range={{ min: 0, max: 2.5 }}
                incrementDecrementVal={0.1}
                value={max}
                setValue={(val) => {
                  dispatch(setProteinCalc({ ...protein, max: val }));
                }}
              />
            </Grid2>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '75%',
                margin: '0 auto'
              }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  fontFamily: 'Calibri',
                  fontSize: '1rem',
                  fontWeight: 400,
                  fontStyle: 'italic'
                }}
              >
                Press{' '}
                <Box
                  component={'img'}
                  src={InfoIconTwo}
                  alt="Info"
                  sx={{ width: 15, height: 15, verticalAlign: 'middle' }}
                />{' '}
                {/* {`button to display more information on disease-specific protein requirements for Body Mass Index <18.5kg/m2 and >30kg/m2`} */}
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      'button to display more information on disease-specific protein requirements for Body Mass Index <18.5kg/m<sup>2</sup> and >30kg/m<sup>2</sup>'
                  }}
                ></span>
              </Typography>
              <Typography
                sx={{
                  textAlign: 'center',
                  fontFamily: 'Calibri',
                  fontSize: '1rem',
                  fontWeight: 400,
                  marginTop: '0.625rem'
                }}
              >
                If there is no guideline available,
                <br />
                PENG recommends using <br />
                <span style={{ fontWeight: '900' }}>
                  1.0-1.5g/kg bodyweight/day
                </span>
              </Typography>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 5 }}
            >
              <DoubleTextBlueCard
                miniHeading1="MIN"
                label1={String(calculatedMin)}
                miniHeading2="MAX"
                unit1={' g/day'}
                label2={String(calculatedMax)}
                unit2={' g/day'}
                icon={ImproveTreatment}
              />
            </Box>
          </ArcContainer>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
          <NavigationButtons
            nextRoute={'/estimating-requirements/fluids'}
            nextDisabled={false}
          />
        </Box>
      </Box>

      <MixedModal
        open={openImageInfoModal}
        onClose={() => setOpenImageInfoModal(false)}
        srcImage={ProteinRequirementImage}
        imageHeight="auto"
        imageAlt="Protein Guide"
        title={'Reference: '}
        content={
          'Todorovic VE. Mafrici B. A Pocket Guide to Clinical Nutrition 2018. 5th edition'
        }
        imageOverflow="auto"
      />
    </>
  );
};
